import { Injectable, inject } from '@angular/core';
import { TranslateService, TranslateStore } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import {
  EAGER_TRANSLATION_MODULES,
  TRANSLATION_MODULE_NAMES,
  TranslationModule,
  normalizeLanguage,
} from './i18n.config';
import { TranslationLoaderService } from './translation-loader.service';
import { TranslationReadyService } from './translation-ready.service';
import { syncLiveTranslations } from './live-translations';

/** Поточна мова у `TranslateService`. */
export function currentLangOf(translate: TranslateService): string {
  return normalizeLanguage(translate.getCurrentLang() ?? translate.getFallbackLang());
}

/**
 * Фасад над `TranslateService` для ліниво-завантажуваних модулів перекладу.
 *
 * Правила гри:
 *  - базовий словник (`<lang>.json`) вантажить сам `TranslateService` через свій лоадер;
 *  - усе інше додається сюди через `setTranslation(lang, chunk, merge = true)`;
 *  - кожен модуль вантажиться максимум один раз на мову (кеш у `TranslationLoaderService`);
 *  - при зміні мови перезавантажуються ЛИШЕ ті модулі, що були реально задіяні,
 *    а не весь набір неймспейсів.
 */
@Injectable({ providedIn: 'root' })
export class TranslationManagerService {
  private readonly translate = inject(TranslateService);
  private readonly store = inject(TranslateStore);
  private readonly loader = inject(TranslationLoaderService);
  private readonly ready = inject(TranslationReadyService);

  /** `<lang>` -> модулі, вже вмерджені у словник цієї мови. */
  private readonly loadedModules = new Map<string, Set<TranslationModule>>();

  /** Запити в польоті, щоб паралельні виклики не породжували дублі мерджів. */
  private readonly inFlight = new Map<string, Observable<boolean>>();

  private initialized = false;

  /**
   * Підписується на зміну мови. Викликається один раз із APP-ініціалізатора.
   */
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const initialLang = currentLangOf(this.translate);
    this.ready.setLanguage(initialLang);
    this.ready.markReady(null, initialLang);
    this.syncStatic(initialLang);

    // Будь-яка зміна словника (базовий файл, домерджений модуль) дзеркалиться
    // у live-translations, щоб константи з enum/constants.ts лишались актуальними.
    this.translate.onTranslationChange.subscribe(({ lang }) => this.syncStatic(normalizeLanguage(lang)));

    this.translate.onLangChange.subscribe(({ lang }) => {
      const language = normalizeLanguage(lang);
      const previouslyLoaded = this.modulesToRehydrate(language);

      // Базовий словник нової мови вже застосований самим TranslateService.
      this.ready.resetForLanguage(language);
      this.ready.setLanguage(language);
      this.ready.markReady(null, language);
      this.syncStatic(language);

      if (previouslyLoaded.length) {
        this.loadModules(previouslyLoaded, language).subscribe();
      }
    });
  }

  /** Модулі, які треба «перезібрати» для нової мови: об'єднання вже задіяних по всіх мовах. */
  private modulesToRehydrate(language: string): TranslationModule[] {
    const union = new Set<TranslationModule>(EAGER_TRANSLATION_MODULES);
    for (const [lang, modules] of this.loadedModules.entries()) {
      if (lang === language) {
        continue;
      }
      modules.forEach((module) => union.add(module));
    }
    this.loadedModules.get(language)?.forEach((module) => union.add(module));
    // Для нової мови нічого ще не вмерджено — стартуємо список з нуля.
    this.loadedModules.set(language, new Set());
    return [...union];
  }

  /**
   * Довантажує переклади модуля й мерджить їх у словник мови.
   * Повторний виклик для вже завантаженого модуля повертається синхронно.
   */
  public loadModuleTranslations(
    module: TranslationModule,
    language?: string,
  ): Observable<boolean> {
    const lang = normalizeLanguage(language ?? currentLangOf(this.translate));

    if (this.isModuleLoaded(module, lang)) {
      return of(true);
    }

    const key = `${lang}|${module}`;
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    const request$ = this.loader.load(lang, module).pipe(
      tap((chunk) => this.applyChunk(lang, module, chunk)),
      map(() => true),
      finalize(() => this.inFlight.delete(key)),
    );

    this.inFlight.set(key, request$);
    return request$;
  }

  /** Паралельно вантажить кілька модулів. */
  public loadModules(modules: readonly TranslationModule[], language?: string): Observable<boolean> {
    if (!modules.length) {
      return of(true);
    }
    return forkJoin(modules.map((module) => this.loadModuleTranslations(module, language))).pipe(
      map(() => true),
    );
  }

  public isModuleLoaded(module: TranslationModule, language?: string): boolean {
    const lang = normalizeLanguage(language ?? currentLangOf(this.translate));
    return Boolean(this.loadedModules.get(lang)?.has(module));
  }

  /**
   * Фонове дозавантаження решти модулів — страхувальна сітка на випадок,
   * коли якийсь екран забули підписати на свій неймспейс.
   * Запускається в idle, тому не конкурує з першим рендером.
   */
  public prefetchRemaining(language?: string): void {
    const lang = normalizeLanguage(language ?? currentLangOf(this.translate));
    const missing = TRANSLATION_MODULE_NAMES.filter((module) => !this.isModuleLoaded(module, lang));
    if (!missing.length) {
      return;
    }

    const run = (): void => {
      this.loadModules(missing, lang).subscribe();
    };

    const idle = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback;

    if (typeof idle === 'function') {
      idle(run, { timeout: 5000 });
    } else {
      setTimeout(run, 2000);
    }
  }

  private syncStatic(language: string): void {
    const isActive = language === currentLangOf(this.translate);
    const dictionary = this.store.getTranslations(language) as Record<string, any>;
    syncLiveTranslations(language, dictionary ?? {}, isActive);
  }

  private applyChunk(language: string, module: TranslationModule, chunk: Record<string, any>): void {
    if (Object.keys(chunk).length) {
      this.translate.setTranslation(language, chunk, true);
    }

    let modules = this.loadedModules.get(language);
    if (!modules) {
      modules = new Set<TranslationModule>();
      this.loadedModules.set(language, modules);
    }
    modules.add(module);

    this.ready.markReady(module, language);
  }
}
